import React, { FC, useCallback, useMemo } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { ICartEntry } from 'features/cart/types';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import {
  AddIconButton,
  StyledButtonBox,
  StyledRecentOrderCard,
} from 'features/menu/pages/recent-items-page/recent-order-card-list/recent-order-card-list-item/styled';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { AvailabilityStatus } from 'components/availability-status';
import { Product } from 'components/product';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import useFoodware from 'hooks/use-foodware';
import { logAddRecentItemToCartEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';

import { MinimalRecentEntryFavoriteButton } from './minimal-recent-entry-favorite-button';
import { MinimalRecentEntryAvailability } from './use-minimal-recent-entry-availability';

interface MinimalRecentOrderCardProps {
  entry: ICartEntry;
  availability: MinimalRecentEntryAvailability;
  checkingAvailability: boolean;
}

const mapSelections = (entry: ICartEntry) => {
  if (!entry.children?.length) {
    return [];
  }

  return entry.children.map((child, index) => ({
    id: `${entry.id}-${child.id}-${index}`,
    element: `${child.quantity}x ${child.name}`,
  }));
};

export const MinimalRecentOrderCard: FC<MinimalRecentOrderCardProps> = ({
  entry,
  availability,
  checkingAvailability,
}) => {
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const { store, noStoreSelected } = useStoreContext();
  const createAriaLabel = useCreateOrderAriaLabel();
  const { isFoodware } = useFoodware();

  const selections = useMemo(() => mapSelections(entry), [entry]);
  const itemIsFoodware = isFoodware({
    _id: entry.id,
    cartId: entry.cartId ?? entry.id,
    lineId: entry.cartId ?? entry.id,
    children: [],
    pickerSelections: {},
    quantity: entry.quantity,
    name: entry.name,
    sanityId: entry.id,
    type: 'Item' as ILegacyCartEntry['type'],
    url: entry.url,
    vendorConfigs: {},
    isExtra: entry.type === 'extra',
  });
  const isAvailable = [
    ItemAvailabilityStatus.AVAILABLE,
    ItemAvailabilityStatus.STORE_NOT_SELECTED,
  ].includes(availability.itemAvailabilityStatus);

  const handleAddToCart = useCallback(() => {
    if (!addMappedEntryToCart(entry)) {
      return;
    }

    logAddRecentItemToCartEvent(entry?.name ?? '', store);
    const message =
      entry.quantity > 1
        ? t('addToCartMultipleSuccess', {
            itemName: entry.name,
            quantity: entry.quantity - 1,
          })
        : t('addToCartSuccess', { itemName: entry.name });
    toaster.addToCartToast(message, entry.image);
  }, [addMappedEntryToCart, entry, store]);

  return (
    <StyledRecentOrderCard>
      <Product
        image={JSON.stringify(entry.image ?? {})}
        name={entry.name}
        quantity={entry.quantity}
        selections={selections}
        displayMode="list"
        note={
          (!noStoreSelected || itemIsFoodware) &&
          !isAvailable && (
            <AvailabilityStatus
              availabilityStatus={availability.itemAvailabilityStatus}
              dayParts={availability.dayParts}
              hideWhenAvailable
              name={entry.name}
            />
          )
        }
        quantityBadgeTextStyle={{
          color: primitive.$mayoEggWhite,
        }}
        quantityBadgeStyle={{
          top: '-10px',
          height: '20px',
          width: '20px',
          justifyContent: 'center',
        }}
      />
      <StyledButtonBox>
        <MinimalRecentEntryFavoriteButton cartEntry={entry} />
        <AddIconButton
          variant="outline"
          onPress={handleAddToCart}
          icon={<Icon variant="add" />}
          disabled={(!noStoreSelected && !isAvailable) || checkingAvailability || itemIsFoodware}
          testID="add-to-cart"
          accessibilityLabel={
            createAriaLabel({
              buttonType: 'add',
              name: entry.name,
              modifiers: selections.map(selection => selection.element),
            }) || t('addToCart')
          }
        />
      </StyledButtonBox>
    </StyledRecentOrderCard>
  );
};
